import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaShoppingCart,
  FaMoneyBillWave,
  FaChartLine,
  FaBoxes,
  FaExclamationTriangle,
  FaSpinner,
  FaArrowRight,
  FaEye,
  FaCalendarTimes,
  FaHourglassHalf,
} from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "https://apigm.sriganapathymedical.shop";

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    products: {
      total: 0,
      totalQuantity: 0,
      lowStock: 0,
    },
    billing: {
      today: {
        bills: 0,
        sales: 0,
        average: 0,
      },
      thisWeek: {
        bills: 0,
        sales: 0,
      },
      thisMonth: {
        bills: 0,
        sales: 0,
      },
      pendingItems: 0,
    },
    lowStockProducts: [],
    expiryAlerts: {
      expired: [],
      expiringSoon: [],
      expiredCount: 0,
      expiringSoonCount: 0
    },
    paymentMethods: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch product statistics
      const productStatsResponse = await axios.get(
        `${API_BASE_URL}/api/products/statistics`
      );

      // Fetch billing statistics
      const billingStatsResponse = await axios.get(
        `${API_BASE_URL}/api/billing/statistics`
      );

      // Fetch low stock products (quantity < 10)
      const lowStockResponse = await axios.get(
        `${API_BASE_URL}/api/products?per_page=100`
      );

      // Process data
      const productStats = productStatsResponse.data;
      const billingStats = billingStatsResponse.data;

      // Filter low stock products using reorder_level
      const allProducts = lowStockResponse.data.items || [];
      const lowStockProducts = allProducts
        .filter(product => {
          const reorderLevel = product.reorder_level || 10;
          return product.quantity <= reorderLevel;
        })
        .sort((a, b) => a.quantity - b.quantity)
        .slice(0, 10);

      // Expiry Logic
      const today = new Date();
      const nextMonth = new Date();
      nextMonth.setMonth(today.getMonth() + 1);

      const expired = [];
      const expiringSoon = [];

      allProducts.forEach(product => {
        if (!product.expiry_date) return;
        const expiryDate = new Date(product.expiry_date);
        if (expiryDate < today) {
          expired.push(product);
        } else if (expiryDate < nextMonth) {
          expiringSoon.push(product);
        }
      });

      // Calculate total payments from payment methods
      const totalPayments = (billingStats.paymentMethods || []).reduce(
        (sum, method) => sum + (method.total || 0),
        0
      );

      setStats({
        products: {
          total: productStats.total_products || 0,
          totalQuantity: productStats.total_quantity || 0,
          lowStock: lowStockProducts.length,
        },
        billing: {
          today: billingStats.today || { bills: 0, sales: 0, average: 0 },
          thisWeek: billingStats.thisWeek || { bills: 0, sales: 0 },
          thisMonth: billingStats.thisMonth || { bills: 0, sales: 0 },
          pendingItems: billingStats.pendingItems || 0,
          totalPayments: totalPayments,
        },
        lowStockProducts: lowStockProducts,
        expiryAlerts: {
          expired: expired,
          expiringSoon: expiringSoon,
          expiredCount: expired.length,
          expiringSoonCount: expiringSoon.length
        },
        paymentMethods: billingStats.paymentMethods || [],
      });

    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleViewAllLowStock = () => {
    navigate('/lowstock'); // Updated to match your NavLink path
  };

  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#0f172a",
      minHeight: "100vh",
      fontFamily: "Inter, system-ui, -apple-system, sans-serif",
      color: "#e2e8f0",
    },
    header: {
      marginBottom: "32px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      margin: 0,
      color: "#ffffff",
      fontSize: "28px",
      fontWeight: "600",
    },
    subtitle: {
      color: "#94a3b8",
      marginTop: "4px",
      fontSize: "14px",
    },
    refreshButton: {
      backgroundColor: "#2563eb",
      color: "white",
      border: "none",
      padding: "10px 20px",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "all 0.2s",
      ':hover': {
        backgroundColor: "#1d4ed8",
      }
    },
    cards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginBottom: "30px",
    },
    card: {
      backgroundColor: "#1e293b",
      padding: "24px",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      display: "flex",
      alignItems: "center",
      gap: "16px",
      transition: "transform 0.2s, box-shadow 0.2s",
      cursor: "pointer",
      border: "1px solid #334155",
    },
    icon: {
      fontSize: "36px",
      padding: "12px",
      borderRadius: "12px",
      backgroundColor: "rgba(255,255,255,0.1)",
    },
    cardContent: {
      flex: 1,
    },
    cardLabel: {
      color: "#94a3b8",
      fontSize: "14px",
      marginBottom: "4px",
    },
    cardValue: {
      color: "#ffffff",
      fontSize: "24px",
      fontWeight: "600",
      margin: 0,
    },
    cardSmallValue: {
      color: "#94a3b8",
      fontSize: "13px",
      marginTop: "4px",
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      gap: "24px",
      marginBottom: "24px",
    },
    tableContainer: {
      backgroundColor: "#1e293b",
      padding: "20px",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      border: "1px solid #334155",
    },
    tableHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    tableTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#ffffff",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    viewAllLink: {
      color: "#10b981",
      fontSize: "14px",
      cursor: "pointer",
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      gap: "4px",
      padding: "6px 12px",
      backgroundColor: "rgba(59, 130, 246, 0.1)",
      borderRadius: "20px",
      transition: "all 0.2s",
      ':hover': {
        backgroundColor: "rgba(59, 130, 246, 0.2)",
      }
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    th: {
      padding: "12px",
      borderBottom: "2px solid #334155",
      textAlign: "left",
      color: "#94a3b8",
      fontWeight: "500",
      fontSize: "13px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    td: {
      padding: "12px",
      borderBottom: "1px solid #334155",
      textAlign: "left",
      fontSize: "14px",
    },
    statusBadge: {
      padding: "4px 8px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "500",
      display: "inline-block",
    },
    lowStock: {
      backgroundColor: "rgba(239, 68, 68, 0.2)",
      color: "#ef4444",
    },
    criticalStock: {
      backgroundColor: "rgba(127, 29, 29, 0.4)",
      color: "#fca5a5",
      border: "1px solid #7f1d1d",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "400px",
      flexDirection: "column",
      gap: "16px",
    },
    errorContainer: {
      backgroundColor: "rgba(239, 68, 68, 0.2)",
      color: "#ef4444",
      padding: "16px",
      borderRadius: "8px",
      marginBottom: "20px",
      border: "1px solid rgba(239, 68, 68, 0.3)",
    },
    spinner: {
      animation: "spin 1s linear infinite",
    },
    paymentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginTop: "16px",
    },
    paymentCard: {
      backgroundColor: "#0f172a",
      padding: "16px",
      borderRadius: "12px",
      border: "1px solid #334155",
    },
    paymentMethod: {
      color: "#94a3b8",
      fontSize: "14px",
      textTransform: "capitalize",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    paymentAmount: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#ffffff",
      marginTop: "8px",
    },
    paymentCount: {
      color: "#94a3b8",
      fontSize: "13px",
      marginTop: "4px",
    },
    viewAllText: {
      color: "#10b981",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      fontSize: "13px",
      transition: "all 0.2s",
      ':hover': {
        color: "#60a5fa",
      }
    },
  };

  if (loading) {
    return (
      <div style={styles.container} className="dashboard-container">
        <div style={styles.loadingContainer}>
          <FaSpinner style={{ ...styles.spinner, fontSize: "40px", color: "#10b981" }} />
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="dashboard-container">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 30px rgba(0,0,0,0.4);
          }
          .view-all:hover {
            background-color: rgba(59, 130, 246, 0.2) !important;
          }
          .view-all-text:hover {
            color: #60a5fa !important;
          }

          /* ============================================================
             MOBILE RESPONSIVENESS
             Inline styles above define the desktop layout and remain
             untouched. Everything below only kicks in on small screens
             and uses !important purely to win over the inline styles
             at those breakpoints.
          ============================================================ */

          /* ---------- Large mobile / tablet portrait (<=768px) ---------- */
          @media (max-width: 768px) {
            .dashboard-container {
              padding: 18px !important;
            }
            .dashboard-header {
              flex-direction: column;
              align-items: stretch !important;
              gap: 12px !important;
              margin-bottom: 22px !important;
            }
            .dashboard-title {
              font-size: 22px !important;
            }
            .dashboard-subtitle {
              font-size: 13px !important;
            }
            .refresh-button {
              width: 100%;
              justify-content: center !important;
              padding: 12px 16px !important;
              font-size: 14px !important;
            }

            .stats-cards {
              grid-template-columns: 1fr !important;
              gap: 14px !important;
              margin-bottom: 18px !important;
            }
            .card {
              padding: 16px !important;
              gap: 14px !important;
              border-radius: 12px !important;
            }
            .stat-icon {
              font-size: 24px !important;
              padding: 10px !important;
              border-radius: 10px !important;
              flex-shrink: 0;
            }
            .stat-content {
              min-width: 0;
              flex: 1;
            }
            .stat-label {
              font-size: 12px !important;
            }
            .stat-value {
              font-size: 19px !important;
              overflow-wrap: anywhere;
            }
            .stat-small {
              font-size: 12px !important;
              overflow-wrap: anywhere;
            }

            .main-grid {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
              margin-bottom: 18px !important;
            }
            .table-container {
              padding: 16px !important;
              border-radius: 12px !important;
            }
            .table-card-header {
              flex-wrap: wrap;
              gap: 10px !important;
              margin-bottom: 14px !important;
            }
            .table-title {
              font-size: 15px !important;
            }
            .view-all {
              font-size: 12px !important;
              padding: 6px 10px !important;
            }
            .view-all-text {
              font-size: 12.5px !important;
            }

            .payment-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 10px !important;
              margin-top: 14px !important;
            }
            .payment-card {
              padding: 12px !important;
            }
            .payment-method {
              font-size: 13px !important;
            }
            .payment-amount {
              font-size: 17px !important;
              overflow-wrap: anywhere;
            }
            .payment-count {
              font-size: 12px !important;
            }
            .payment-summary {
              padding: 14px !important;
            }
          }

          /* ---------- Stacked "card row" tables for narrow screens ---------- */
          @media (max-width: 640px) {
            .data-table thead {
              display: none;
            }
            .data-table,
            .data-table tbody,
            .data-table tr,
            .data-table td {
              display: block;
              width: 100%;
            }
            .data-table {
              min-width: 0 !important;
            }
            .data-row {
              background-color: #0f172a;
              border: 1px solid #334155;
              border-radius: 10px;
              padding: 4px 14px;
              margin-bottom: 10px;
            }
            .data-row td {
              display: flex !important;
              justify-content: space-between;
              align-items: center;
              gap: 12px;
              padding: 9px 0 !important;
              border-bottom: 1px solid rgba(51, 65, 85, 0.5) !important;
              font-size: 13px !important;
              text-align: right;
            }
            .data-row td:last-child {
              border-bottom: none !important;
            }
            .data-row td::before {
              content: attr(data-label);
              color: #94a3b8;
              font-weight: 500;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.4px;
              text-align: left;
              flex-shrink: 0;
            }
            .message-row {
              margin-bottom: 0;
            }
            .message-row td {
              display: block !important;
              text-align: center !important;
            }
            .message-row td::before {
              content: none;
            }
          }

          /* ---------- Medium phones (<=480px) ---------- */
          @media (max-width: 480px) {
            .dashboard-container {
              padding: 14px !important;
            }
            .dashboard-title {
              font-size: 20px !important;
            }
            .dashboard-subtitle {
              font-size: 12px !important;
            }
            .stats-cards {
              gap: 12px !important;
            }
            .card {
              padding: 14px !important;
              gap: 12px !important;
            }
            .stat-icon {
              font-size: 20px !important;
              padding: 9px !important;
            }
            .stat-value {
              font-size: 18px !important;
            }
            .table-container {
              padding: 14px !important;
            }
            .table-title {
              font-size: 14px !important;
            }
            .payment-grid {
              grid-template-columns: 1fr !important;
            }
            .payment-amount {
              font-size: 16px !important;
            }
            .data-row td {
              font-size: 12.5px !important;
            }
          }

          /* ---------- Small phones (<=360px) ---------- */
          @media (max-width: 360px) {
            .dashboard-container {
              padding: 10px !important;
            }
            .dashboard-title {
              font-size: 18px !important;
            }
            .refresh-button {
              font-size: 13px !important;
              padding: 10px 14px !important;
            }
            .card {
              padding: 12px !important;
              gap: 10px !important;
            }
            .stat-icon {
              font-size: 18px !important;
              padding: 7px !important;
            }
            .stat-value {
              font-size: 16px !important;
            }
            .stat-label, .stat-small {
              font-size: 11px !important;
            }
            .table-container {
              padding: 12px !important;
            }
            .data-row {
              padding: 2px 10px !important;
            }
            .data-row td {
              font-size: 12px !important;
            }
            .data-row td::before {
              font-size: 10px !important;
            }
          }
       `}
      </style>

      {/* Header */}
      <div style={styles.header} className="dashboard-header">
        <div>
          <h2 style={styles.title} className="dashboard-title">Dashboard</h2>
          <p style={styles.subtitle} className="dashboard-subtitle">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
        <button
          style={styles.refreshButton}
          className="refresh-button"
          onClick={fetchDashboardData}
        >
          <FaChartLine /> Refresh Data
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.errorContainer} className="error-container">
          <FaExclamationTriangle style={{ marginRight: "8px" }} />
          {error}
        </div>
      )}

      {/* Cards */}
      <div style={styles.cards} className="stats-cards">
        <div className="card" style={styles.card}>
          <FaBoxes style={{ ...styles.icon, color: "#10b981" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">Total Products</div>
            <div style={styles.cardValue} className="stat-value">{stats.products.total}</div>
            <div style={styles.cardSmallValue} className="stat-small">
              {stats.products.totalQuantity} units in stock
            </div>
          </div>
        </div>

        <div className="card" style={styles.card}>
          <FaShoppingCart style={{ ...styles.icon, color: "#f59e0b" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">Today's Sales</div>
            <div style={styles.cardValue} className="stat-value">
              {formatCurrency(stats.billing.today.sales)}
            </div>
            <div style={styles.cardSmallValue} className="stat-small">
              {stats.billing.today.bills} bills · Avg {formatCurrency(stats.billing.today.average)}
            </div>
          </div>
        </div>

        <div className="card" style={styles.card}>
          <FaMoneyBillWave style={{ ...styles.icon, color: "#10b981" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">Total Payments</div>
            <div style={styles.cardValue} className="stat-value">
              {formatCurrency(stats.billing.totalPayments || 0)}
            </div>
            <div style={styles.cardSmallValue} className="stat-small">
              All time payments received
            </div>
          </div>
        </div>

        <div className="card" style={styles.card}>
          <FaChartLine style={{ ...styles.icon, color: "#8b5cf6" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">This Month</div>
            <div style={styles.cardValue} className="stat-value">
              {formatCurrency(stats.billing.thisMonth.sales)}
            </div>
            <div style={styles.cardSmallValue} className="stat-small">
              {stats.billing.thisMonth.bills} bills · {stats.billing.pendingItems} pending items
            </div>
          </div>
        </div>
      </div>

      {/* Expiry Cards */}
      <div style={{ ...styles.cards, marginTop: '20px' }} className="stats-cards">
        <div className="card" style={{ ...styles.card, borderLeft: '4px solid #ef4444' }}>
          <FaCalendarTimes style={{ ...styles.icon, color: "#ef4444" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">Expired Medicines</div>
            <div style={{ ...styles.cardValue, color: '#ef4444' }} className="stat-value">{stats.expiryAlerts.expiredCount}</div>
            <div style={styles.cardSmallValue} className="stat-small">Immediate action required</div>
          </div>
        </div>

        <div className="card" style={{ ...styles.card, borderLeft: '4px solid #f59e0b' }}>
          <FaHourglassHalf style={{ ...styles.icon, color: "#f59e0b" }} className="stat-icon" />
          <div style={styles.cardContent} className="stat-content">
            <div style={styles.cardLabel} className="stat-label">Expiring Soon</div>
            <div style={{ ...styles.cardValue, color: '#f59e0b' }} className="stat-value">{stats.expiryAlerts.expiringSoonCount}</div>
            <div style={styles.cardSmallValue} className="stat-small">Expires within 30 days</div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div style={styles.grid2} className="main-grid">
        {/* Low Stock Products */}
        <div style={styles.tableContainer} className="table-container">
          <div style={styles.tableHeader} className="table-card-header">
            <h3 style={styles.tableTitle} className="table-title">
              <FaExclamationTriangle color="#ef4444" />
              Low Stock Alert ({stats.lowStockProducts.length} items)
            </h3>
            <span
              style={styles.viewAllLink}
              onClick={handleViewAllLowStock}
              className="view-all"
            >
              View All <FaArrowRight size={12} />
            </span>
          </div>
          <div className="table-scroll">
            <table style={styles.table} className="data-table">
              <thead>
                <tr>
                  <th style={styles.th}>Product</th>
                  <th style={styles.th}>Model</th>
                  <th style={styles.th}>Stock</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats.lowStockProducts.length > 0 ? (
                  stats.lowStockProducts.slice(0, 5).map((product) => (
                    <tr key={product.id} className="data-row">
                      <td style={styles.td} data-label="Product">
                        <div style={{ fontWeight: "500" }}>{product.name}</div>
                      </td>
                      <td style={styles.td} data-label="Model">
                        <span style={{ color: "#94a3b8" }}>{product.model || '-'}</span>
                      </td>
                      <td style={styles.td} data-label="Stock">
                        <span style={{
                          fontWeight: "600",
                          color: product.quantity === 0 ? "#ef4444" : "#f59e0b"
                        }}>
                          {product.quantity}
                        </span>
                      </td>
                      <td style={styles.td} data-label="Status">
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...(product.quantity === 0
                              ? styles.criticalStock
                              : styles.lowStock),
                          }}
                        >
                          {product.quantity === 0 ? "Out of Stock" : "Low Stock"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="message-row">
                    <td colSpan="4" style={{ ...styles.td, textAlign: "center", padding: "40px" }}>
                      <FaBoxes size={32} style={{ opacity: 0.5, marginBottom: "12px" }} />
                      <div>All products are well stocked ✓</div>
                      <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "8px" }}>
                        No items with quantity less than 10
                      </div>
                    </td>
                  </tr>
                )}

                {stats.lowStockProducts.length > 5 && (
                  <tr className="message-row">
                    <td colSpan="4" style={{ ...styles.td, textAlign: "center", backgroundColor: "#0f172a" }}>
                      <span
                        onClick={handleViewAllLowStock}
                        style={styles.viewAllText}
                        className="view-all-text"
                      >
                        <FaEye size={12} /> View all {stats.lowStockProducts.length} low stock items
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Methods Summary */}
        <div style={styles.tableContainer} className="table-container">
          <div style={styles.tableHeader} className="table-card-header">
            <h3 style={styles.tableTitle} className="table-title">
              <FaMoneyBillWave color="#10b981" />
              Payment Methods
            </h3>
          </div>

          {stats.paymentMethods.length > 0 ? (
            <div style={styles.paymentGrid} className="payment-grid">
              {stats.paymentMethods.map((method, index) => (
                <div key={index} style={styles.paymentCard} className="payment-card">
                  <div style={styles.paymentMethod} className="payment-method">
                    <span style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor:
                        method.method === 'cash' ? '#10b981' :
                          method.method === 'card' ? '#10b981' :
                            method.method === 'upi' ? '#8b5cf6' : '#f59e0b'
                    }} />
                    {method.method}
                  </div>
                  <div style={styles.paymentAmount} className="payment-amount">
                    {formatCurrency(method.total)}
                  </div>
                  <div style={styles.paymentCount} className="payment-count">
                    {method.count} {method.count === 1 ? 'transaction' : 'transactions'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <FaMoneyBillWave size={32} style={{ opacity: 0.5, marginBottom: "12px" }} />
              <p style={{ color: "#94a3b8" }}>No payment data available</p>
            </div>
          )}

          {/* Total Payments Summary */}
          {stats.paymentMethods.length > 0 && (
            <div
              className="payment-summary"
              style={{
                marginTop: "20px",
                padding: "16px",
                backgroundColor: "#0f172a",
                borderRadius: "12px",
                border: "1px solid #334155"
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: "#94a3b8" }}>Total Payments</span>
                <span style={{ fontSize: "20px", fontWeight: "600", color: "#10b981" }}>
                  {formatCurrency(stats.billing.totalPayments || 0)}
                </span>
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                All time total
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Expiry Alerts Table */}
      {(stats.expiryAlerts.expiredCount > 0 || stats.expiryAlerts.expiringSoonCount > 0) && (
        <div style={{ ...styles.tableContainer, marginTop: '24px' }} className="table-container">
          <div style={styles.tableHeader} className="table-card-header">
            <h3 style={styles.tableTitle} className="table-title">
              <FaCalendarTimes color="#ef4444" />
              Recent Expiry Alerts
            </h3>
          </div>
          <div className="table-scroll">
            <table style={styles.table} className="data-table">
              <thead>
                <tr>
                  <th style={styles.th}>Medicine</th>
                  <th style={styles.th}>Batch</th>
                  <th style={styles.th}>Expiry Date</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[...stats.expiryAlerts.expired, ...stats.expiryAlerts.expiringSoon].slice(0, 5).map((product) => {
                  const isExpired = new Date(product.expiry_date) < new Date();
                  return (
                    <tr key={product.id} className="data-row">
                      <td style={styles.td} data-label="Medicine">
                        <div style={{ fontWeight: "500" }}>{product.name}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{product.model}</div>
                      </td>
                      <td style={styles.td} data-label="Batch">{product.batch_no || '-'}</td>
                      <td style={styles.td} data-label="Expiry Date">{product.expiry_date}</td>
                      <td style={styles.td} data-label="Status">
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor: isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                            color: isExpired ? '#ef4444' : '#f59e0b',
                          }}
                        >
                          {isExpired ? "Expired" : "Expiring Soon"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;